import { useState, useEffect } from 'react'
// import PropTypes from 'prop-types'
import { saveError } from 'shared/sentry'

import { useFlags } from 'launchdarkly-react-client-sdk'
import { log } from 'shared/logging'
import { LOG_LEVELS, RESULT_CODES } from 'constants/values'
import authService from 'authentication/service'
import {
  handleSaveProductSelection,
  handleSaveProductIframeActions,
  mapCardVerifyResponse,
  handleManageAutopayIframeActions,
  handleSaveAutopay,
} from './processDebitCardUtils'

const UseDebitCardSubmitHandle = ({
  setAttemptStatus,
  attemptStatus,
  applicationNumber,
  // product specific
  debitCardProduct,
  updateDebitCardProduct,
  closeModal,
  setNewRepayment,
  setNewAutopayMethod,
}) => {
  const [paylianceActions, setPaylianceActions] = useState(null)
  const [paylianceActionsResults, setPaylianceActionsResults] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isDisabled, setIsDisabled] = useState(true)
  const [paylianceTokenizeResult, setPaylianceTokenizeResult] = useState(null)
  const [hasClosedLoans, setHasClosedLoans] = useState(false)
  const [isProcessingIframeResult, setIsProcessingIframeResult] = useState(
    false
  )

  const { showMessageForPaylianceIframeErrors } = useFlags()

  const handlePaylianceIframeActions = () => {
    if (isDisabled || isLoading) return

    setIsDisabled(true)

    if (!!debitCardProduct) {
      handleSaveProductIframeActions(
        setAttemptStatus,
        setIsDisabled,
        setNewRepayment,
        closeModal,
        attemptStatus
      )
    } else {
      handleManageAutopayIframeActions(
        attemptStatus,
        setAttemptStatus,
        setIsDisabled
      )
    }

    if (!paylianceActionsResults && !isDisabled) {
      setIsDisabled(true)
      paylianceActions && paylianceActions.validate()
    }
  }

  useEffect(() => {
    if (paylianceActions) {
      setTimeout(() => {
        setIsDisabled(false)
      }, 2500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paylianceActions])

  useEffect(() => {
    const auth = authService.getAuthAttributes()
    auth.then(result => {
      setHasClosedLoans(result.hasClosedLoans)
    })
    if (!paylianceActionsResults || isProcessingIframeResult) return

    setIsProcessingIframeResult(true)
    try {
      if (
        paylianceActionsResults.event === 'validate' &&
        paylianceActionsResults.successful
      ) {
        paylianceActions.submit()
      }

      if (
        paylianceActionsResults.event === 'tokenize' &&
        paylianceActionsResults.successful
      ) {
        const { cardId, cardLastFour, expirationDate } = paylianceActionsResults
        setPaylianceTokenizeResult({ cardId, cardLastFour, expirationDate })
        paylianceActions.verify(
          paylianceActionsResults.cardId,
          paylianceActionsResults.cvv,
          applicationNumber
        )
      }

      if (
        paylianceActionsResults.event === 'verify' &&
        paylianceActionsResults.successful &&
        paylianceTokenizeResult
      ) {
        const cardVerifyResponse = mapCardVerifyResponse(
          paylianceActionsResults
        )

        const { cardId, cardLastFour, expirationDate } = paylianceTokenizeResult

        setPaylianceTokenizeResult(null)

        const debitCardDetails = {
          lastFourDigitOfCard: cardLastFour,
          expiryDate: expirationDate,
          token: cardId,
        }

        const verifyCardDetails = {
          ...cardVerifyResponse,
        }

        if (!!debitCardProduct) {
          debitCardProduct.debitCardDetails = debitCardDetails
          debitCardProduct.verifyCardDetails = verifyCardDetails
          updateDebitCardProduct({
            ...debitCardProduct,
            debitCardDetails,
            verifyCardDetails,
          })
        }

        if (cardId) {
          const verifyPaylianceCard = async () => {
            try {
              setIsLoading(true)
              if (!!debitCardProduct) {
                await handleSaveProductSelection(
                  debitCardProduct,
                  setAttemptStatus
                )
              } else {
                const request = {
                  cardTokenNumber: cardId,
                  lastFourDigitOfCard: cardLastFour,
                  cardExpiryDate: expirationDate,
                  cardVerifyResponse,
                }
                if (setNewAutopayMethod) {
                  setNewAutopayMethod({ type: 'DC', value: cardLastFour })
                }
                await handleSaveAutopay(
                  applicationNumber,
                  'DC',
                  request,
                  setAttemptStatus
                )
              }

              setTimeout(() => {
                setIsDisabled(false)
                setIsLoading(false)
              }, 2500)
            } catch (error) {
              saveError(error)
              if (!!debitCardProduct) {
                setAttemptStatus(RESULT_CODES.CARD_ERROR_1)
              } else {
                setAttemptStatus('RETRY')
              }
              setTimeout(() => {
                setIsDisabled(false)
                setIsLoading(false)
              }, 2500)
            }
          }
          verifyPaylianceCard()
        }
      }

      const knownEvents = ['validate', 'tokenize', 'verify']
      const monitoringEvents = ['tokenize', 'verify']
      const iframeErrorEvents = showMessageForPaylianceIframeErrors
        ? ['tokenize', 'verify']
        : []

      if (
        knownEvents.includes(paylianceActionsResults.event) &&
        !paylianceActionsResults.successful
      ) {
        setIsDisabled(false)

        iframeErrorEvents.includes(paylianceActionsResults.event) &&
          setAttemptStatus(RESULT_CODES.ERROR_IFRAME)

        monitoringEvents.includes(paylianceActionsResults.event) &&
          saveError(
            `Payliance Card Iframe error - Monitoring: ${JSON.stringify(
              paylianceActionsResults
            )}`
          )
      }

      if (paylianceActionsResults.event === 'failure') {
        log(
          `Payliance Card Iframe error: ${JSON.stringify(
            paylianceActionsResults
          )}`,
          LOG_LEVELS.INFO
        )
      }
    } finally {
      setPaylianceActionsResults(null)
      setIsProcessingIframeResult(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paylianceActionsResults])

  return [
    handlePaylianceIframeActions,
    setPaylianceActions,
    setPaylianceActionsResults,
    isLoading,
    isDisabled,
    hasClosedLoans,
  ]
}

export default UseDebitCardSubmitHandle
